import React from "react";
import { useStoreState } from "ezStore";
import { intl } from "LocaleWrapper";
import { useApolloClient } from "@apollo/client";

import { Select } from "@cofano/cck";
import { SelectProps } from "@cofano/cck/Select/Select";

import FavoriteSelectOption from "components/selects/FavoriteSelectOption";
import { getRelationFavorites } from "components/selects/utils";

import { ChildPropsFromPagination, Favorite } from "types/commons";
import { GetRelationsForSelectDocument, GetRelationsForSelectQuery } from "__generated__/view/relations/graphql/relation.queries.generated";
import { FavoriteType, RelationType } from "__generated__/enums";

type BaseRelationOption = ChildPropsFromPagination<GetRelationsForSelectQuery, "relationsByPartialName">;

export type RelationSelectOption = Omit<BaseRelationOption, "contacts"> & {
	contacts?: NonNullable<BaseRelationOption["contacts"]> | null;
};

export type RelationSelectSearch = {
	invoiceLineCreditor?: boolean;
	invoiceLineDebtor?: boolean;
}

const RelationSelect = <Multiple extends boolean = false,>({
	types = [],
	showEmail,
	additionalRelations,
	search,
	...rest
}: Omit<SelectProps<RelationSelectOption, Multiple>, "fetchOptions" | "paginated" | "getOptionLabel" | "renderOption"> & {
	types?: Array<RelationType>;
	showEmail?: boolean;
	additionalRelations?: RelationSelectOption[];
	search?: RelationSelectSearch;
}) => {

	const client = useApolloClient();
	const sessionFavorites = useStoreState((store) => store.session.user?.properties.favorites);
	const favorites = getRelationFavorites(sessionFavorites ?? [], types);

	const onFetchOptions = async(params: string, page: number) => {
		try  {
			const currentPage = page - 1;

			const result = await client.query({
				query: GetRelationsForSelectDocument,
				variables: {
					name: params,
					types: types,
					pagination: {
						page: currentPage,
						size: 10,
					},
					invoiceLineCreditor: search?.invoiceLineCreditor,
					invoiceLineDebtor: search?.invoiceLineDebtor,
				},
			});

			const options: RelationSelectOption[] = [];

			if (currentPage === 0 && additionalRelations) {
				additionalRelations.forEach(value => options.push(value));
			}

			if (currentPage === 0 && !params) {
				favorites.forEach((favorite: Favorite) => {
					options.push({
						...favorite,
						id: favorite.entityId,
						name: favorite.label || "unknown",
					});
				});
			}

			result.data.relationsByPartialName.content.forEach(item => options.push(item as RelationSelectOption));

			return {
				options: options,
				totalResults: result.data.relationsByPartialName?.totalElements ?? 0,
			};
		} catch (response) {
			return {
				options: [],
				totalResults: 0,
			};
		}
	};

	return (
		<Select
			fetchOptions={onFetchOptions}
			getOptionLabel={option => option.name}
			typeToSearchLabel={intl.formatMessage({ id: "TYPE_TO_SEARCH" })}
			loadingLabel={intl.formatMessage({ id: "LOADING_DATA" })}
			popoverHeight={300}
			renderOption={(props, option) => (
				<li {...props}>
					<FavoriteSelectOption
						entityId={option.id}
						entityType={FavoriteType.Relation}>
						{showEmail && option.email ? (
							<div>
								<div>{option.name}</div>
								<small>{option.email}</small>
							</div>
						) : (
							<React.Fragment>
								{option.name}
							</React.Fragment>
						)}
					</FavoriteSelectOption>
				</li>
			)}
			{...rest}
			paginated
		/>
	);
};

export default RelationSelect;
